// src/types/Order.ts
import { VariantSelection } from './product';

export interface OrderItem {
  productId: string;
  productName: string;
  productSku: string;
  productImage?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  // Variant support
  variantId?: string;
  variantSelections?: VariantSelection[];
  variantSku?: string;
  // Warehouse information
  sourceWarehouseId?: string;
  sourceWarehouseName?: string;
}

export interface ShippingAddress {
  fullName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber?: string;
}

export interface Order {
  id?: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerWhatsApp: string;
  items: OrderItem[];
  subtotal: number;
  taxAmount: number;
  shippingCost: number;
  discountAmount: number;
  totalAmount: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  shippingAddress: ShippingAddress;
  billingAddress?: ShippingAddress;
  notes?: string;
  trackingNumber?: string;
  estimatedDelivery?: Date;
  orderDate: Date;
  updatedAt?: Date;
  createdBy: string;
  // Cancellation/Return info
  cancellationReason?: CancellationReason;
  returnReason?: ReturnReason;
  damagedItems?: DamagedItem[];
  // Warehouse allocation status
  isWarehouseAllocated?: boolean;
  allocatedWarehouseId?: string;
  allocatedWarehouseName?: string;
  // Tax and Discount metadata (optional - for recalculation purposes)
  taxType?: 'fixed' | 'percentage';
  taxPercentage?: number;
  discountType?: 'fixed' | 'percentage';
  discountPercentage?: number;
  // Payment proof attachment (for bank_transfer payment method)
  paymentProofUrl?: string;
  paymentProofType?: 'image' | 'pdf';
  paymentProofFileName?: string;
}

export interface DamagedItem {
  productId: string;
  variantId?: string;
  productName: string;
  quantity: number;
  reason: string;
  notes?: string;
}

export type OrderStatus = 
  | 'pending' 
  | 'confirmed' 
  | 'processing' 
  | 'shipped' 
  | 'delivered' 
  | 'cancelled' 
  | 'returned';

export type PaymentStatus = 
  | 'pending' 
  | 'paid' 
  | 'failed' 
  | 'refunded' 
  | 'partially_refunded';

export type PaymentMethod = 
  | 'credit_card' 
  | 'debit_card' 
  | 'paypal' 
  | 'bank_transfer' 
  | 'cash_on_delivery' 
  | 'crypto';

export type CancellationReason = 
  | 'customer_request' 
  | 'out_of_stock' 
  | 'price_error' 
  | 'duplicate_order' 
  | 'delivery_issue' 
  | 'payment_failed' 
  | 'fraudulent' 
  | 'other';

export type ReturnReason = 
  | 'wrong_item' 
  | 'defective' 
  | 'damaged' 
  | 'not_as_described' 
  | 'size_issue' 
  | 'changed_mind' 
  | 'late_delivery' 
  | 'other';

export interface OrderFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerWhatsApp: string;
  items: {
    productId: string;
    variantId?: string;
    quantity: string;
  }[];
  subtotal: string;
  taxAmount: string;
  shippingCost: string;
  discountAmount: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  shippingAddress: ShippingAddress;
  billingAddress: ShippingAddress;
  notes: string;
  trackingNumber: string;
  estimatedDelivery: string;
  // Warehouse allocation for order processing
  allocatedWarehouseId?: string;
  // Tax and Discount metadata
  taxType?: 'fixed' | 'percentage';
  taxPercentage?: string;
  discountType?: 'fixed' | 'percentage';
  discountPercentage?: string;
  // Payment proof attachment (for bank_transfer payment method)
  paymentProofUrl?: string;
  paymentProofType?: 'image' | 'pdf';
  paymentProofFileName?: string;
}

export interface DamagedItemRecord {
  id?: string;
  orderId: string;
  items: DamagedItem[]; // This is an array of damaged items
  reason: ReturnReason;
  notes?: string;
  createdAt?: Date;
}

// Labels for UI
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: 'Pending',
  paid: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  paypal: 'PayPal',
  bank_transfer: 'Bank Transfer',
  cash_on_delivery: 'Cash on Delivery',
  crypto: 'Cryptocurrency',
};

export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  customer_request: 'Customer Request',
  out_of_stock: 'Out of Stock',
  price_error: 'Price Error',
  duplicate_order: 'Duplicate Order',
  delivery_issue: 'Delivery Issue',
  payment_failed: 'Payment Failed',
  fraudulent: 'Fraudulent Order',
  other: 'Other',
};

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  wrong_item: 'Wrong Item',
  defective: 'Defective',
  damaged: 'Damaged',
  not_as_described: 'Not as Described',
  size_issue: 'Size Issue',
  changed_mind: 'Changed Mind',
  late_delivery: 'Late Delivery',
  other: 'Other',
};

// Warehouse selection interface
export interface WarehouseStockInfo {
  warehouseId: string;
  warehouseName: string;
  availableQuantity: number;
  totalStock: number;
}

export interface ProductStockAvailability {
  productId: string;
  variantId?: string;
  productName: string;
  variantName?: string;
  requestedQuantity: number;
  warehouses: WarehouseStockInfo[];
  canFulfill: boolean;
}

// // src/types/Order.ts
// import { VariantSelection } from './product';

// export interface OrderItem {
//   productId: string;
//   productName: string;
//   productSku: string;
//   productImage?: string;
//   quantity: number;
//   unitPrice: number;
//   totalPrice: number;
//   // Variant support
//   variantId?: string;
//   variantSelections?: VariantSelection[];
//   variantSku?: string;
//   // Warehouse information
//   sourceWarehouseId?: string;
//   sourceWarehouseName?: string;
// }

// export interface ShippingAddress {
//   fullName: string;
//   addressLine1: string;
//   addressLine2?: string;
//   city: string;
//   state: string;
//   postalCode: string;
//   country: string;
//   phoneNumber?: string;
// }

// export interface Order {
//   id?: string;
//   orderNumber: string;
//   customerName: string;
//   customerEmail: string;
//   customerPhone?: string;
//   items: OrderItem[];
//   subtotal: number;
//   taxAmount: number;
//   shippingCost: number;
//   discountAmount: number;
//   totalAmount: number;
//   status: OrderStatus;
//   paymentStatus: PaymentStatus;
//   paymentMethod: PaymentMethod;
//   shippingAddress: ShippingAddress;
//   billingAddress?: ShippingAddress;
//   notes?: string;
//   trackingNumber?: string;
//   estimatedDelivery?: Date;
//   orderDate: Date;
//   updatedAt?: Date;
//   createdBy: string;
//   // Cancellation/Return info
//   cancellationReason?: CancellationReason;
//   returnReason?: ReturnReason;
//   damagedItems?: DamagedItem[];
//   // Warehouse allocation status
//   isWarehouseAllocated?: boolean;
//   allocatedWarehouseId?: string;
//   allocatedWarehouseName?: string;
//   // Tax and Discount metadata (optional - for recalculation purposes)
//   taxType?: 'fixed' | 'percentage';
//   taxPercentage?: number;
//   discountType?: 'fixed' | 'percentage';
//   discountPercentage?: number;
// }

// export interface DamagedItem {
//   productId: string;
//   variantId?: string;
//   productName: string;
//   quantity: number;
//   reason: string;
//   notes?: string;
// }

// export type OrderStatus = 
//   | 'pending' 
//   | 'confirmed' 
//   | 'processing' 
//   | 'shipped' 
//   | 'delivered' 
//   | 'cancelled' 
//   | 'returned';

// export type PaymentStatus = 
//   | 'pending' 
//   | 'paid' 
//   | 'failed' 
//   | 'refunded' 
//   | 'partially_refunded';

// export type PaymentMethod = 
//   | 'credit_card' 
//   | 'debit_card' 
//   | 'bank_transfer' 
//   | 'cash_on_delivery' 

// export type CancellationReason = 
//   | 'customer_request' 
//   | 'out_of_stock' 
//   | 'price_error' 
//   | 'duplicate_order' 
//   | 'delivery_issue' 
//   | 'payment_failed' 
//   | 'fraudulent' 
//   | 'other';

// export type ReturnReason = 
//   | 'wrong_item' 
//   | 'defective' 
//   | 'damaged' 
//   | 'not_as_described' 
//   | 'size_issue' 
//   | 'changed_mind' 
//   | 'late_delivery' 
//   | 'other';

// export interface OrderFormData {
//   customerName: string;
//   customerEmail: string;
//   customerPhone: string;
//   items: {
//     productId: string;
//     variantId?: string;
//     quantity: string;
//   }[];
//   subtotal: string;
//   taxAmount: string;
//   shippingCost: string;
//   discountAmount: string;
//   status: OrderStatus;
//   paymentStatus: PaymentStatus;
//   paymentMethod: PaymentMethod;
//   shippingAddress: ShippingAddress;
//   billingAddress: ShippingAddress;
//   notes: string;
//   trackingNumber: string;
//   estimatedDelivery: string;
//   // Warehouse allocation for order processing
//   allocatedWarehouseId?: string;
//   // Tax and Discount metadata
//   taxType?: 'fixed' | 'percentage';
//   taxPercentage?: string;
//   discountType?: 'fixed' | 'percentage';
//   discountPercentage?: string;
// }

// export interface DamagedItemRecord {
//   id?: string;
//   orderId: string;
//   items: DamagedItem[]; // This is an array of damaged items
//   reason: ReturnReason;
//   notes?: string;
//   createdAt?: Date;
// }

// // Labels for UI
// export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
//   pending: 'Pending',
//   confirmed: 'Confirmed',
//   processing: 'Processing',
//   shipped: 'Shipped',
//   delivered: 'Delivered',
//   cancelled: 'Cancelled',
//   returned: 'Returned',
// };

// export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
//   pending: 'Pending',
//   paid: 'Paid',
//   failed: 'Failed',
//   refunded: 'Refunded',
//   partially_refunded: 'Partially Refunded',
// };

// export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
//   credit_card: 'Credit Card',
//   debit_card: 'Debit Card',
//   bank_transfer: 'Bank Transfer',
//   cash_on_delivery: 'Cash on Delivery',
// };

// export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
//   customer_request: 'Customer Request',
//   out_of_stock: 'Out of Stock',
//   price_error: 'Price Error',
//   duplicate_order: 'Duplicate Order',
//   delivery_issue: 'Delivery Issue',
//   payment_failed: 'Payment Failed',
//   fraudulent: 'Fraudulent Order',
//   other: 'Other',
// };

// export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
//   wrong_item: 'Wrong Item',
//   defective: 'Defective',
//   damaged: 'Damaged',
//   not_as_described: 'Not as Described',
//   size_issue: 'Size Issue',
//   changed_mind: 'Changed Mind',
//   late_delivery: 'Late Delivery',
//   other: 'Other',
// };

// // Warehouse selection interface
// export interface WarehouseStockInfo {
//   warehouseId: string;
//   warehouseName: string;
//   availableQuantity: number;
//   totalStock: number;
// }

// export interface ProductStockAvailability {
//   productId: string;
//   variantId?: string;
//   productName: string;
//   variantName?: string;
//   requestedQuantity: number;
//   warehouses: WarehouseStockInfo[];
//   canFulfill: boolean;
// }